import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Currency conversion (USD to NGN)
const USD_TO_NGN_RATE = 1600; // Approximate rate - can be updated dynamically

const formatCurrency = (amount, decimals = 2) => {
  const ngnAmount = (amount || 0) * USD_TO_NGN_RATE;
  return `₦${ngnAmount.toLocaleString('en-NG', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
};

const ConsolidatedAIInsights = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [period, setPeriod] = useState('daily');
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Merchant usage state
  const [merchantsUsage, setMerchantsUsage] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [merchantSearch, setMerchantSearch] = useState('');
  const [merchantPage, setMerchantPage] = useState(1);
  const [merchantPagination, setMerchantPagination] = useState({ total: 0, pages: 0 });

  // Quota management state
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [merchantDetails, setMerchantDetails] = useState(null);
  const [quotaForm, setQuotaForm] = useState({
    dailyQueryLimit: 100,
    weeklyQueryLimit: 500,
    monthlyQueryLimit: 2000,
    yearlyQueryLimit: 20000,
    dailyTokenLimit: 100000,
    weeklyTokenLimit: 500000,
    monthlyTokenLimit: 2000000,
    yearlyTokenLimit: 20000000,
    dailyCostLimit: 10.00,
    weeklyCostLimit: 50.00,
    monthlyCostLimit: 200.00,
    yearlyCostLimit: 2000.00,
    isUnlimited: false,
    isBlocked: false,
    blockReason: ''
  });
  const [savingQuota, setSavingQuota] = useState(false);
  const [quotaMessage, setQuotaMessage] = useState(null);

  // Global settings state
  const [globalSettings, setGlobalSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState(null);

  // All merchants search state (for settings tab)
  const [allMerchants, setAllMerchants] = useState([]);
  const [allMerchantsSearch, setAllMerchantsSearch] = useState('');
  const [allMerchantsLoading, setAllMerchantsLoading] = useState(false);
  const [allMerchantsPagination, setAllMerchantsPagination] = useState({ total: 0, pages: 0, page: 1 });

  // Fetch comprehensive AI insights
  const fetchInsights = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      // Try enhanced insights first, fallback to basic insights
      let response = await fetch(`/api/admin/ai/insights?timeRange=${timeRange}&period=${period}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('AI Insights API failed:', response.status, errorText);
        throw new Error(`AI Insights API failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('AI Insights API Response:', data);
      setInsights(data.data || data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching AI insights:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, [timeRange, period]);

  // Fetch merchants usage
  const fetchMerchantsUsage = async () => {
    setMerchantsLoading(true);
    try {
      const params = new URLSearchParams({
        timeRange,
        page: merchantPage,
        limit: 20,
        ...(merchantSearch && { search: merchantSearch })
      });

      const response = await fetch(`/api/admin/ai/merchants/usage?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch merchant usage');

      const data = await response.json();
      setMerchantsUsage(data.data?.merchants || []);
      setMerchantPagination(data.data?.pagination || { total: 0, pages: 0 });
    } catch (err) {
      console.error('Error fetching merchant usage:', err);
    } finally {
      setMerchantsLoading(false);
    }
  };

  // Fetch specific merchant details
  const fetchMerchantDetails = async (merchantId) => {
    try {
      // Try to get usage data first
      const usageResponse = await fetch(`/api/admin/ai/merchants/${merchantId}/usage?timeRange=${timeRange}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (usageResponse.ok) {
        const data = await usageResponse.json();
        setMerchantDetails(data.data);

        // Set quota form values
        if (data.data?.quota) {
          setQuotaForm({
            ...quotaForm,
            ...data.data.quota
          });
        } else {
          // Reset to defaults if no quota
          setQuotaForm({
            dailyQueryLimit: 100,
            weeklyQueryLimit: 500,
            monthlyQueryLimit: 2000,
            yearlyQueryLimit: 20000,
            dailyTokenLimit: 100000,
            weeklyTokenLimit: 500000,
            monthlyTokenLimit: 2000000,
            yearlyTokenLimit: 20000000,
            dailyCostLimit: 10.00,
            weeklyCostLimit: 50.00,
            monthlyCostLimit: 200.00,
            yearlyCostLimit: 2000.00,
            isUnlimited: false,
            isBlocked: false,
            blockReason: ''
          });
        }
      } else {
        // If no usage data, get quota info directly
        const quotaResponse = await fetch(`/api/admin/ai/quotas/${merchantId}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });

        if (quotaResponse.ok) {
          const quotaData = await quotaResponse.json();
          setMerchantDetails({
            merchant: quotaData.data?.merchant || { id: merchantId, username: 'Unknown', email: '' },
            usage: { daily: { queries: 0 }, weekly: { queries: 0 }, monthly: { queries: 0 }, yearly: { queries: 0 } },
            quota: quotaData.data
          });

          if (quotaData.data) {
            setQuotaForm({
              ...quotaForm,
              ...quotaData.data
            });
          }
        } else {
          // Just show empty data with defaults
          setMerchantDetails({
            merchant: { id: merchantId, username: 'Merchant', email: '' },
            usage: { daily: { queries: 0 }, weekly: { queries: 0 }, monthly: { queries: 0 }, yearly: { queries: 0 } },
            quota: null
          });
        }
      }
    } catch (err) {
      console.error('Error fetching merchant details:', err);
      // Still set merchant details so modal shows
      setMerchantDetails({
        merchant: { id: merchantId, username: 'Merchant', email: '' },
        usage: { daily: { queries: 0 }, weekly: { queries: 0 }, monthly: { queries: 0 }, yearly: { queries: 0 } },
        quota: null
      });
    }
  };

  // Save quota
  const saveQuota = async () => {
    if (!selectedMerchant) return;

    setSavingQuota(true);
    setQuotaMessage(null);

    try {
      const response = await fetch(`/api/admin/ai/quotas/${selectedMerchant}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(quotaForm)
      });

      if (!response.ok) throw new Error('Failed to save quota');

      setQuotaMessage({ type: 'success', text: 'Quota updated successfully!' });
      fetchMerchantsUsage();
    } catch (err) {
      setQuotaMessage({ type: 'error', text: err.message });
    } finally {
      setSavingQuota(false);
    }
  };

  // Toggle merchant block
  const toggleMerchantBlock = async (merchantId, blocked, reason = '') => {
    try {
      const response = await fetch(`/api/admin/ai/merchants/${merchantId}/block`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ blocked, reason })
      });

      if (!response.ok) throw new Error('Failed to update block status');

      fetchMerchantsUsage();
      if (selectedMerchant === merchantId) {
        fetchMerchantDetails(merchantId);
      }
    } catch (err) {
      console.error('Error toggling block:', err);
    }
  };

  // Load merchants when tab changes
  useEffect(() => {
    if (activeTab === 'merchants') {
      fetchMerchantsUsage();
    }
  }, [activeTab, merchantPage, timeRange]);

  // Debounced search
  useEffect(() => {
    if (activeTab === 'merchants') {
      const timer = setTimeout(() => {
        setMerchantPage(1);
        fetchMerchantsUsage();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [merchantSearch]);

  // Load merchant details when selected
  useEffect(() => {
    if (selectedMerchant) {
      fetchMerchantDetails(selectedMerchant);
    } else {
      setMerchantDetails(null);
    }
  }, [selectedMerchant]);

  // Fetch global settings
  const fetchGlobalSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await fetch('/api/admin/ai/settings', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to fetch settings');

      const data = await response.json();
      setGlobalSettings(data.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Save global settings
  const saveGlobalSettings = async () => {
    setSavingSettings(true);
    setSettingsMessage(null);

    try {
      const response = await fetch('/api/admin/ai/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(globalSettings)
      });

      if (!response.ok) throw new Error('Failed to save settings');

      setSettingsMessage({ type: 'success', text: 'Global settings saved successfully!' });
    } catch (err) {
      setSettingsMessage({ type: 'error', text: err.message });
    } finally {
      setSavingSettings(false);
    }
  };

  // Search all merchants
  const searchAllMerchants = async (page = 1) => {
    setAllMerchantsLoading(true);
    try {
      const params = new URLSearchParams({
        q: allMerchantsSearch,
        page,
        limit: 10
      });

      const response = await fetch(`/api/admin/ai/merchants/search?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to search merchants');

      const data = await response.json();
      setAllMerchants(data.data?.merchants || []);
      setAllMerchantsPagination({ ...data.data?.pagination, page });
    } catch (err) {
      console.error('Error searching merchants:', err);
    } finally {
      setAllMerchantsLoading(false);
    }
  };

  // Load settings when tab changes to settings
  useEffect(() => {
    if (activeTab === 'settings') {
      fetchGlobalSettings();
      searchAllMerchants();
    }
  }, [activeTab]);

  // Debounced merchant search for settings tab
  useEffect(() => {
    if (activeTab === 'settings') {
      const timer = setTimeout(() => {
        searchAllMerchants(1);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [allMerchantsSearch]);

  // Chart configurations
  const merchantPerformanceConfig = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Merchant AI Performance',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Prediction Count'
        }
      }
    },
  };

  const confidenceDistributionConfig = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Query Success Distribution',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
    },
  };

  const usageByTypeConfig = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'AI Usage by Interaction Type',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Queries'
        }
      }
    },
  };

  const costBreakdownConfig = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Cost Breakdown by User Type',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
    },
  };

  const dailyTrendsConfig = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Daily Usage Trends',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Number of Queries'
        }
      }
    },
  };

  const guardrailTriggersConfig = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      },
      title: {
        display: true,
        text: 'Guardrail Triggers',
        font: {
          size: 16,
          weight: 'bold'
        }
      },
    },
  };

  // Prepare chart data
  const getMerchantPerformanceData = () => {
    if (!insights?.topMerchants || insights.topMerchants.length === 0) return null;

    const merchants = insights.topMerchants.slice(0, 10);

    return {
      labels: merchants.map(merchant => {
        const name = merchant.merchantName || merchant.name || 'Merchant';
        return name.length > 15 ? name.substring(0, 15) + '...' : name;
      }),
      datasets: [
        {
          label: 'Query Count',
          data: merchants.map(merchant => merchant.totalQueries || merchant.predictionCount || 0),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgb(54, 162, 235)',
          borderWidth: 1,
        },
        {
          label: 'Total Tokens (K)',
          data: merchants.map(merchant => Math.round((merchant.totalTokens || 0) / 1000)),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgb(75, 192, 192)',
          borderWidth: 1,
        }
      ]
    };
  };

  const getConfidenceDistributionData = () => {
    if (!insights?.summary) return null;

    const { successfulQueries, failedQueries, totalQueries } = insights.summary;

    return {
      labels: ['Successful', 'Failed'],
      datasets: [
        {
          data: [
            successfulQueries || 0,
            failedQueries || 0
          ],
          backgroundColor: [
            'rgba(75, 192, 192, 0.8)',
            'rgba(255, 99, 132, 0.8)'
          ],
          borderColor: [
            'rgb(75, 192, 192)',
            'rgb(255, 99, 132)'
          ],
          borderWidth: 2,
        }
      ]
    };
  };

  const getUsageByTypeData = () => {
    return {
      labels: insights?.usageByType?.map(item => item._id) || ['Prediction', 'Chatbot', 'General', 'Image Generation'],
      datasets: [{
        label: 'Queries',
        data: insights?.usageByType?.map(item => item.count) || [0, 0, 0, 0],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)'
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(239, 68, 68, 1)'
        ],
        borderWidth: 1
      }]
    };
  };

  const getCostBreakdownData = () => {
    // Handle both detailed breakdown (legacy) and simple format (new)
    const costBreakdown = insights?.costBreakdown;
    let merchantCost = 0, customerCost = 0, adminCost = 0;

    if (costBreakdown?.merchantCosts) {
      merchantCost = costBreakdown.merchantCosts.totalCost || 0;
      customerCost = costBreakdown.customerCosts?.totalCost || 0;
      adminCost = costBreakdown.adminCosts?.totalCost || 0;
    } else {
      // New format - total cost, estimate breakdown as percentage
      const totalCost = costBreakdown?.totalCost || insights?.summary?.totalCost || 0;
      merchantCost = totalCost * 0.7; // Estimate: 70% merchant usage
      customerCost = totalCost * 0.2; // Estimate: 20% customer usage
      adminCost = totalCost * 0.1; // Estimate: 10% admin usage
    }

    return {
      labels: ['Merchants', 'Customers', 'Admin'],
      datasets: [{
        data: [merchantCost, customerCost, adminCost],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)'
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)'
        ],
        borderWidth: 1
      }]
    };
  };

  const getDailyTrendsData = () => {
    const trends = insights?.dailyTrends || [];
    return {
      labels: trends.map(item => {
        // Handle different date formats from backend
        if (item._id?.month && item._id?.day) {
          return `${item._id.month}/${item._id.day}`;
        } else if (item.date) {
          const date = new Date(item.date);
          return `${date.getMonth() + 1}/${date.getDate()}`;
        }
        return '';
      }),
      datasets: [
        {
          label: 'Total Queries',
          data: trends.map(item => item.queries || 0),
          borderColor: 'rgba(59, 130, 246, 1)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4
        },
        {
          label: 'Successful Queries',
          data: trends.map(item => item.successfulQueries || 0),
          borderColor: 'rgba(16, 185, 129, 1)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4
        }
      ]
    };
  };

  const getGuardrailTriggersData = () => {
    // Handle both object format (new) and array format (legacy)
    const triggers = insights?.guardrailTriggers;
    let contentFiltered = 0, rateLimited = 0, usageLimitExceeded = 0, fallbackUsed = 0;

    if (Array.isArray(triggers)) {
      contentFiltered = triggers.find(g => g._id?.contentFiltered)?.count || 0;
      rateLimited = triggers.find(g => g._id?.rateLimited)?.count || 0;
      usageLimitExceeded = triggers.find(g => g._id?.usageLimitExceeded)?.count || 0;
      fallbackUsed = triggers.find(g => g._id?.fallbackUsed)?.count || 0;
    } else if (triggers) {
      contentFiltered = triggers.contentFiltered || 0;
      rateLimited = triggers.rateLimited || 0;
      usageLimitExceeded = triggers.usageLimitExceeded || 0;
      fallbackUsed = triggers.fallbackUsed || 0;
    }

    return {
      labels: ['Content Filtered', 'Rate Limited', 'Usage Limit Exceeded', 'Fallback Used'],
      datasets: [{
        data: [contentFiltered, rateLimited, usageLimitExceeded, fallbackUsed],
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(156, 163, 175, 0.8)',
          'rgba(59, 130, 246, 0.8)'
        ],
        borderColor: [
          'rgba(239, 68, 68, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(156, 163, 175, 1)',
          'rgba(59, 130, 246, 1)'
        ],
        borderWidth: 1
      }]
    };
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'text-green-600 bg-green-100';
    if (confidence >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getAlertTypeColor = (type) => {
    switch (type) {
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          padding: '24px', 
          textAlign: 'center',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>✨</div>
          <div style={{ fontSize: '16px', color: '#6b7280' }}>Loading AI insights...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          padding: '24px', 
          textAlign: 'center',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '16px', color: '#ef4444' }}>⚠️</div>
          <div style={{ fontSize: '16px', marginBottom: '16px' }}>Failed to load AI insights</div>
          <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>{error}</div>
          <button
            onClick={() => fetchInsights()}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        padding: '24px', 
        marginBottom: '24px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700' }}>
              ✨ AI Insights Dashboard
            </h1>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
              Comprehensive AI analytics, usage monitoring, and performance metrics
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px 12px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '8px 12px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <button
              onClick={() => fetchInsights(true)}
              disabled={refreshing}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: refreshing ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {refreshing ? (
                <div style={{ 
                  width: '16px', 
                  height: '16px', 
                  border: '2px solid #ffffff40', 
                  borderTop: '2px solid #ffffff', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite' 
                }} />
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                  <path d="M21 3v5h-5"/>
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                  <path d="M3 21v-5h5"/>
                </svg>
              )}
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', 
            color: 'white', 
            padding: '20px', 
            borderRadius: '8px' 
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>Total Queries</div>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{insights?.summary?.totalQueries || 0}</div>
          </div>

          <div style={{ 
            background: 'linear-gradient(135deg, #10b981, #059669)', 
            color: 'white', 
            padding: '20px', 
            borderRadius: '8px' 
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>Avg Confidence</div>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{Math.round(insights?.summary?.avgConfidence || 0)}%</div>
          </div>

          <div style={{ 
            background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', 
            color: 'white', 
            padding: '20px', 
            borderRadius: '8px' 
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>Success Rate</div>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{Math.round(insights?.summary?.successRate || 0)}%</div>
          </div>

          <div style={{
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            color: 'white',
            padding: '20px',
            borderRadius: '8px'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>Total Cost</div>
            <div style={{ fontSize: '24px', fontWeight: '700' }}>{formatCurrency(insights?.summary?.totalCost || 0)}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        padding: '24px', 
        marginBottom: '24px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {['overview', 'merchants', 'usage', 'costs', 'compliance', 'performance', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: activeTab === tab ? '#3b82f6' : '#f3f4f6',
                color: activeTab === tab ? 'white' : '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                textTransform: 'capitalize'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '24px'
          }}>
            {/* Merchant Performance Chart */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              minHeight: '300px'
            }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Merchant Performance</h4>
              {getMerchantPerformanceData() ? (
                <Bar data={getMerchantPerformanceData()} options={merchantPerformanceConfig} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#9ca3af' }}>
                  No merchant AI usage data yet
                </div>
              )}
            </div>

            {/* Query Success Distribution Chart */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              minHeight: '300px'
            }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Query Success Distribution</h4>
              {getConfidenceDistributionData() ? (
                <Doughnut data={getConfidenceDistributionData()} options={confidenceDistributionConfig} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#9ca3af' }}>
                  No query data available
                </div>
              )}
            </div>

            {/* Usage by Type Chart */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              minHeight: '300px'
            }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Usage by Type</h4>
              {insights?.usageByType?.length > 0 ? (
                <Bar data={getUsageByTypeData()} options={usageByTypeConfig} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#9ca3af' }}>
                  No usage type data available
                </div>
              )}
            </div>

            {/* Cost Breakdown Chart */}
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              minHeight: '300px'
            }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Cost Breakdown</h4>
              {(insights?.summary?.totalCost > 0 || insights?.costBreakdown?.totalCost > 0) ? (
                <Doughnut data={getCostBreakdownData()} options={costBreakdownConfig} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#9ca3af' }}>
                  No cost data available
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'merchants' && (
          <div>
            {/* Merchant Details Modal */}
            {selectedMerchant && merchantDetails && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}>
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  maxWidth: '900px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflow: 'auto'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600' }}>
                        {merchantDetails.merchant?.username || 'Unknown Merchant'}
                      </h3>
                      <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                        {merchantDetails.merchant?.email}
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedMerchant(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        color: '#6b7280'
                      }}
                    >
                      &times;
                    </button>
                  </div>

                  {/* Usage Summary Cards */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{ background: '#eff6ff', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#3b82f6', marginBottom: '4px' }}>Today</div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af' }}>
                        {merchantDetails.usage?.daily?.queries || 0}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>queries</div>
                    </div>
                    <div style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#16a34a', marginBottom: '4px' }}>This Week</div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#166534' }}>
                        {merchantDetails.usage?.weekly?.queries || 0}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>queries</div>
                    </div>
                    <div style={{ background: '#fef3c7', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#d97706', marginBottom: '4px' }}>This Month</div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#92400e' }}>
                        {merchantDetails.usage?.monthly?.queries || 0}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>queries</div>
                    </div>
                    <div style={{ background: '#fdf2f8', padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#db2777', marginBottom: '4px' }}>This Year</div>
                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#9d174d' }}>
                        {merchantDetails.usage?.yearly?.queries || 0}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>queries</div>
                    </div>
                  </div>

                  {/* Cost Summary */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>Daily Cost</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>{formatCurrency(merchantDetails.usage?.daily?.cost || 0)}</div>
                    </div>
                    <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>Weekly Cost</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>{formatCurrency(merchantDetails.usage?.weekly?.cost || 0)}</div>
                    </div>
                    <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>Monthly Cost</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>{formatCurrency(merchantDetails.usage?.monthly?.cost || 0)}</div>
                    </div>
                    <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>Yearly Cost</div>
                      <div style={{ fontSize: '16px', fontWeight: '600' }}>{formatCurrency(merchantDetails.usage?.yearly?.cost || 0)}</div>
                    </div>
                  </div>

                  {/* Quota Settings */}
                  <div style={{
                    background: '#f8fafc',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '24px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                      Quota Settings
                    </h4>

                    {quotaMessage && (
                      <div style={{
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '16px',
                        backgroundColor: quotaMessage.type === 'success' ? '#dcfce7' : '#fee2e2',
                        color: quotaMessage.type === 'success' ? '#166534' : '#991b1b'
                      }}>
                        {quotaMessage.text}
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={quotaForm.isUnlimited}
                          onChange={(e) => setQuotaForm({ ...quotaForm, isUnlimited: e.target.checked })}
                        />
                        <span style={{ fontSize: '14px' }}>Unlimited Usage</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={quotaForm.isBlocked}
                          onChange={(e) => setQuotaForm({ ...quotaForm, isBlocked: e.target.checked })}
                        />
                        <span style={{ fontSize: '14px', color: '#dc2626' }}>Block AI Access</span>
                      </label>
                    </div>

                    {!quotaForm.isUnlimited && (
                      <>
                        <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '12px', color: '#374151' }}>
                          Query Limits
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
                          <div>
                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Daily</label>
                            <input
                              type="number"
                              value={quotaForm.dailyQueryLimit}
                              onChange={(e) => setQuotaForm({ ...quotaForm, dailyQueryLimit: parseInt(e.target.value) })}
                              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Weekly</label>
                            <input
                              type="number"
                              value={quotaForm.weeklyQueryLimit}
                              onChange={(e) => setQuotaForm({ ...quotaForm, weeklyQueryLimit: parseInt(e.target.value) })}
                              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Monthly</label>
                            <input
                              type="number"
                              value={quotaForm.monthlyQueryLimit}
                              onChange={(e) => setQuotaForm({ ...quotaForm, monthlyQueryLimit: parseInt(e.target.value) })}
                              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Yearly</label>
                            <input
                              type="number"
                              value={quotaForm.yearlyQueryLimit}
                              onChange={(e) => setQuotaForm({ ...quotaForm, yearlyQueryLimit: parseInt(e.target.value) })}
                              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                            />
                          </div>
                        </div>

                        <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '12px', color: '#374151' }}>
                          Cost Limits (NGN)
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
                          <div>
                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Daily</label>
                            <input
                              type="number"
                              step="0.01"
                              value={quotaForm.dailyCostLimit}
                              onChange={(e) => setQuotaForm({ ...quotaForm, dailyCostLimit: parseFloat(e.target.value) })}
                              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Weekly</label>
                            <input
                              type="number"
                              step="0.01"
                              value={quotaForm.weeklyCostLimit}
                              onChange={(e) => setQuotaForm({ ...quotaForm, weeklyCostLimit: parseFloat(e.target.value) })}
                              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Monthly</label>
                            <input
                              type="number"
                              step="0.01"
                              value={quotaForm.monthlyCostLimit}
                              onChange={(e) => setQuotaForm({ ...quotaForm, monthlyCostLimit: parseFloat(e.target.value) })}
                              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                            />
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#6b7280' }}>Yearly</label>
                            <input
                              type="number"
                              step="0.01"
                              value={quotaForm.yearlyCostLimit}
                              onChange={(e) => setQuotaForm({ ...quotaForm, yearlyCostLimit: parseFloat(e.target.value) })}
                              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setSelectedMerchant(null)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveQuota}
                        disabled={savingQuota}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: 'none',
                          backgroundColor: '#0d7463',
                          color: 'white',
                          cursor: savingQuota ? 'not-allowed' : 'pointer',
                          opacity: savingQuota ? 0.6 : 1
                        }}
                      >
                        {savingQuota ? 'Saving...' : 'Save Quota'}
                      </button>
                    </div>
                  </div>

                  {/* Usage by Type */}
                  {merchantDetails.usageByType && merchantDetails.usageByType.length > 0 && (
                    <div>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600' }}>Usage by Type</h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {merchantDetails.usageByType.map((item, idx) => (
                          <div key={idx} style={{
                            background: '#f3f4f6',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '13px'
                          }}>
                            <span style={{ fontWeight: '500' }}>{item.type}</span>
                            <span style={{ color: '#6b7280', marginLeft: '8px' }}>{item.count} queries</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Search and Filters */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                Merchant AI Usage
              </h3>
              <input
                type="text"
                placeholder="Search merchants..."
                value={merchantSearch}
                onChange={(e) => setMerchantSearch(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  width: '250px',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Merchants Table */}
            {merchantsLoading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                Loading merchant data...
              </div>
            ) : merchantsUsage.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                No merchants found with AI usage.
              </div>
            ) : (
              <>
                <div style={{
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden'
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Merchant</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Queries</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Success</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Tokens</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Cost</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Last Query</th>
                        <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {merchantsUsage.map((merchant, index) => (
                        <tr key={merchant.merchantId || index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb' }}>
                          <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ fontWeight: '500' }}>{merchant.merchantName}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{merchant.merchantEmail}</div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontWeight: '600' }}>
                            {merchant.totalQueries?.toLocaleString() || 0}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                            {merchant.totalQueries > 0 ? Math.round((merchant.successfulQueries / merchant.totalQueries) * 100) : 0}%
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                            {merchant.totalTokens?.toLocaleString() || 0}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}>
                            {formatCurrency(merchant.totalCost || 0)}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontSize: '12px', color: '#6b7280' }}>
                            {merchant.lastQueryAt ? new Date(merchant.lastQueryAt).toLocaleDateString() : 'Never'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>
                            <button
                              onClick={() => setSelectedMerchant(merchant.merchantId)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '4px',
                                border: 'none',
                                backgroundColor: '#0d7463',
                                color: 'white',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              Manage
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {merchantPagination.pages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                    <button
                      onClick={() => setMerchantPage(p => Math.max(1, p - 1))}
                      disabled={merchantPage === 1}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        cursor: merchantPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: merchantPage === 1 ? 0.5 : 1
                      }}
                    >
                      Previous
                    </button>
                    <span style={{ padding: '8px 12px', color: '#6b7280' }}>
                      Page {merchantPage} of {merchantPagination.pages}
                    </span>
                    <button
                      onClick={() => setMerchantPage(p => Math.min(merchantPagination.pages, p + 1))}
                      disabled={merchantPage === merchantPagination.pages}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        backgroundColor: 'white',
                        cursor: merchantPage === merchantPagination.pages ? 'not-allowed' : 'pointer',
                        opacity: merchantPage === merchantPagination.pages ? 0.5 : 1
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'usage' && (
          <div>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Daily Usage Trends
            </h3>
            <div style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              marginBottom: '24px',
              minHeight: '300px'
            }}>
              {insights?.dailyTrends?.length > 0 ? (
                <Line data={getDailyTrendsData()} options={dailyTrendsConfig} />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '250px', color: '#9ca3af' }}>
                  No daily usage data available yet. Data will appear as merchants use AI features.
                </div>
              )}
            </div>

            <div>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                Top Merchants by AI Usage
              </h4>
              <div style={{
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #e2e8f0',
                minHeight: '150px'
              }}>
                {insights?.topMerchants?.length > 0 ? (
                  insights.topMerchants.map((merchant, index) => (
                    <div key={merchant._id || index} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: index < (insights.topMerchants?.length || 0) - 1 ? '1px solid #e2e8f0' : 'none'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500' }}>{merchant.merchantName || merchant.name || 'Unknown'}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          Tokens: {(merchant.totalTokens || 0).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: '600' }}>{merchant.totalQueries || merchant.predictionCount || 0} queries</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {formatCurrency(merchant.totalCost || 0)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100px', color: '#9ca3af' }}>
                    No merchant AI usage data yet
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'costs' && (
          <div>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Cost Analysis
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {(insights?.summary?.totalCost > 0 || insights?.costBreakdown?.totalCost > 0) ? (
                  <Doughnut data={getCostBreakdownData()} options={costBreakdownConfig} />
                ) : (
                  <div style={{ color: '#9ca3af', textAlign: 'center' }}>
                    No cost data available yet
                  </div>
                )}
              </div>
              <div style={{
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                  Cost Breakdown
                </h4>
                {(() => {
                  const costBreakdown = insights?.costBreakdown;
                  const totalCost = costBreakdown?.totalCost || insights?.summary?.totalCost || 0;
                  const merchantCost = costBreakdown?.merchantCosts?.totalCost || totalCost * 0.7;
                  const customerCost = costBreakdown?.customerCosts?.totalCost || totalCost * 0.2;
                  const adminCost = costBreakdown?.adminCosts?.totalCost || totalCost * 0.1;
                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>Merchants:</span>
                        <span>{formatCurrency(merchantCost)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>Customers:</span>
                        <span>{formatCurrency(customerCost)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <span>Admin:</span>
                        <span>{formatCurrency(adminCost)}</span>
                      </div>
                      <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600' }}>
                        <span>Total:</span>
                        <span>{formatCurrency(totalCost)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'compliance' && (
          <div>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Guardrail Triggers
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              <div>
                <Doughnut data={getGuardrailTriggersData()} options={guardrailTriggersConfig} />
              </div>
              <div style={{ 
                backgroundColor: '#f8fafc', 
                borderRadius: '8px', 
                padding: '16px',
                border: '1px solid #e2e8f0'
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                  Compliance Metrics
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Content Filtered:</span>
                  <span>{insights?.compliance?.contentFiltered || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Rate Limited:</span>
                  <span>{insights?.compliance?.rateLimited || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Usage Limit Exceeded:</span>
                  <span>{insights?.compliance?.usageLimitExceeded || 0}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>Fallback Used:</span>
                  <span>{insights?.compliance?.fallbackUsed || 0}</span>
                </div>
                <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600' }}>
                  <span>Compliance Rate:</span>
                  <span>{insights?.compliance?.complianceRate || 0}%</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && (
          <div>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>
              Performance Metrics
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ 
                backgroundColor: '#f0f9ff', 
                padding: '16px', 
                borderRadius: '8px',
                border: '1px solid #bae6fd'
              }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#0c4a6e' }}>
                  {insights?.performanceMetrics?.avgResponseTime || 0}ms
                </div>
                <div style={{ fontSize: '14px', color: '#64748b' }}>Avg Response Time</div>
              </div>
              <div style={{ 
                backgroundColor: '#f0fdf4', 
                padding: '16px', 
                borderRadius: '8px',
                border: '1px solid #bbf7d0'
              }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#166534' }}>
                  {Math.round(insights?.performanceMetrics?.avgConfidence || insights?.summary?.avgConfidence || 0)}%
                </div>
                <div style={{ fontSize: '14px', color: '#64748b' }}>Avg Confidence</div>
              </div>
              <div style={{ 
                backgroundColor: '#fef3c7', 
                padding: '16px', 
                borderRadius: '8px',
                border: '1px solid #fde68a'
              }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#92400e' }}>
                  {Math.round(insights?.performanceMetrics?.avgTokensPerQuery || 0)}
                </div>
                <div style={{ fontSize: '14px', color: '#64748b' }}>Avg Tokens/Query</div>
              </div>
              <div style={{
                backgroundColor: '#fdf2f8',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #fce7f3'
              }}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#be185d' }}>
                  {formatCurrency(insights?.performanceMetrics?.avgCostPerQuery || 0)}
                </div>
                <div style={{ fontSize: '14px', color: '#64748b' }}>Avg Cost/Query</div>
              </div>
            </div>
            
            {insights?.errorAnalysis && insights.errorAnalysis.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                  Error Analysis
                </h4>
                <div style={{ 
                  backgroundColor: '#f8fafc', 
                  borderRadius: '8px', 
                  padding: '16px',
                  border: '1px solid #e2e8f0'
                }}>
                  {insights.errorAnalysis.map((error, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '8px 0',
                      borderBottom: index < insights.errorAnalysis.length - 1 ? '1px solid #e2e8f0' : 'none'
                    }}>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '14px' }}>
                          {error._id || 'Unknown Error'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          Types: {error.interactionTypes?.join(', ') || 'N/A'}
                        </div>
                      </div>
                      <div style={{ fontWeight: '600', color: '#ef4444' }}>
                        {error.count} occurrences
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <h3 style={{ margin: '0 0 24px 0', fontSize: '18px', fontWeight: '600' }}>
              AI Settings & Quota Management
            </h3>

            {/* Global Default Settings */}
            <div style={{
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                Global Default Limits
              </h4>
              <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6b7280' }}>
                These limits apply to all merchants who don't have custom quotas set.
              </p>

              {settingsMessage && (
                <div style={{
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  backgroundColor: settingsMessage.type === 'success' ? '#dcfce7' : '#fee2e2',
                  color: settingsMessage.type === 'success' ? '#166534' : '#991b1b'
                }}>
                  {settingsMessage.text}
                </div>
              )}

              {settingsLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>Loading settings...</div>
              ) : globalSettings ? (
                <>
                  <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '12px', color: '#374151' }}>
                    Default Query Limits
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280' }}>Daily</label>
                      <input
                        type="number"
                        value={globalSettings.defaultDailyLimit || 100}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, defaultDailyLimit: parseInt(e.target.value) })}
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280' }}>Weekly</label>
                      <input
                        type="number"
                        value={globalSettings.defaultWeeklyLimit || 500}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, defaultWeeklyLimit: parseInt(e.target.value) })}
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280' }}>Monthly</label>
                      <input
                        type="number"
                        value={globalSettings.defaultMonthlyLimit || 2000}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, defaultMonthlyLimit: parseInt(e.target.value) })}
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '12px', color: '#6b7280' }}>Yearly</label>
                      <input
                        type="number"
                        value={globalSettings.defaultYearlyLimit || 20000}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, defaultYearlyLimit: parseInt(e.target.value) })}
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={globalSettings.contentFilteringEnabled}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, contentFilteringEnabled: e.target.checked })}
                      />
                      <span style={{ fontSize: '14px' }}>Enable Content Filtering</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                        checked={globalSettings.rateLimitingEnabled}
                        onChange={(e) => setGlobalSettings({ ...globalSettings, rateLimitingEnabled: e.target.checked })}
                      />
                      <span style={{ fontSize: '14px' }}>Enable Rate Limiting</span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={saveGlobalSettings}
                      disabled={savingSettings}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: '#0d7463',
                        color: 'white',
                        cursor: savingSettings ? 'not-allowed' : 'pointer',
                        opacity: savingSettings ? 0.6 : 1
                      }}
                    >
                      {savingSettings ? 'Saving...' : 'Save Global Settings'}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>No settings found</div>
              )}
            </div>

            {/* Merchant Quota Management */}
            <div style={{
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              padding: '20px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                Manage Individual Merchant Quotas
              </h4>
              <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6b7280' }}>
                Search for any merchant to set custom AI limits or block access.
              </p>

              <input
                type="text"
                placeholder="Search merchants by name or email..."
                value={allMerchantsSearch}
                onChange={(e) => setAllMerchantsSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  marginBottom: '16px',
                  fontSize: '14px'
                }}
              />

              {allMerchantsLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>Searching...</div>
              ) : allMerchants.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                  {allMerchantsSearch ? 'No merchants found' : 'Enter a search term to find merchants'}
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {allMerchants.map((merchant) => (
                      <div
                        key={merchant.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          backgroundColor: 'white',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: '500' }}>{merchant.username}</div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>{merchant.email}</div>
                          {merchant.hasQuota && (
                            <span style={{
                              display: 'inline-block',
                              marginTop: '4px',
                              padding: '2px 6px',
                              backgroundColor: merchant.quota?.isBlocked ? '#fef2f2' : '#dcfce7',
                              color: merchant.quota?.isBlocked ? '#991b1b' : '#166534',
                              fontSize: '10px',
                              borderRadius: '4px'
                            }}>
                              {merchant.quota?.isBlocked ? 'Blocked' : merchant.quota?.isUnlimited ? 'Unlimited' : 'Custom Quota'}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => setSelectedMerchant(merchant.id)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '4px',
                            border: 'none',
                            backgroundColor: '#0d7463',
                            color: 'white',
                            fontSize: '12px',
                            cursor: 'pointer'
                          }}
                        >
                          {merchant.hasQuota ? 'Edit Quota' : 'Set Quota'}
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {allMerchantsPagination.pages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                      <button
                        onClick={() => searchAllMerchants(allMerchantsPagination.page - 1)}
                        disabled={allMerchantsPagination.page === 1}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                          backgroundColor: 'white',
                          cursor: allMerchantsPagination.page === 1 ? 'not-allowed' : 'pointer',
                          opacity: allMerchantsPagination.page === 1 ? 0.5 : 1
                        }}
                      >
                        Previous
                      </button>
                      <span style={{ padding: '6px', color: '#6b7280', fontSize: '14px' }}>
                        Page {allMerchantsPagination.page} of {allMerchantsPagination.pages}
                      </span>
                      <button
                        onClick={() => searchAllMerchants(allMerchantsPagination.page + 1)}
                        disabled={allMerchantsPagination.page === allMerchantsPagination.pages}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                          backgroundColor: 'white',
                          cursor: allMerchantsPagination.page === allMerchantsPagination.pages ? 'not-allowed' : 'pointer',
                          opacity: allMerchantsPagination.page === allMerchantsPagination.pages ? 0.5 : 1
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Recent Predictions */}
      {insights?.recentPredictions && insights.recentPredictions.length > 0 && (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          padding: '24px', 
          marginBottom: '24px',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600' }}>Recent AI Predictions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {insights.recentPredictions.map((prediction, index) => (
              <div key={index} style={{ 
                border: '1px solid #e5e7eb', 
                borderRadius: '8px', 
                padding: '16px',
                backgroundColor: '#f9fafb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>
                    {prediction.merchantId?.username || 'Unknown Merchant'}
                  </span>
                  <span style={{ 
                    padding: '4px 8px', 
                    borderRadius: '4px', 
                    fontSize: '12px', 
                    fontWeight: '600',
                    backgroundColor: getConfidenceColor(prediction.prediction?.confidence || 0),
                    color: 'white'
                  }}>
                    {Math.round(prediction.prediction?.confidence || 0)}% confidence
                  </span>
                </div>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#6b7280' }}>
                  {prediction.suggestion}
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#9ca3af' }}>
                  <span>{new Date(prediction.createdAt).toLocaleDateString()}</span>
                  {prediction.alertType && (
                    <span style={{ 
                      padding: '2px 6px', 
                      borderRadius: '4px', 
                      backgroundColor: getAlertTypeColor(prediction.alertType),
                      color: '#374151'
                    }}>
                      {prediction.alertType}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* System Health */}
      {insights && (
        <div style={{ 
          background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)', 
          borderRadius: '12px', 
          padding: '24px', 
          border: '1px solid #0ea5e9',
          marginTop: '24px'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#0c4a6e' }}>
            ✨ AI System Health
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '16px' 
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#0c4a6e' }}>
                {insights.summary?.totalQueries || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#0c4a6e' }}>Total AI Queries</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#0c4a6e' }}>
                {Math.round(insights.summary?.avgConfidence || 0)}%
              </div>
              <div style={{ fontSize: '14px', color: '#0c4a6e' }}>Average Confidence Score</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#0c4a6e' }}>
                {insights.topMerchants?.length || 0}
              </div>
              <div style={{ fontSize: '14px', color: '#0c4a6e' }}>Active AI Users</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#0c4a6e' }}>
                {Math.round(insights.summary?.successRate || 0)}%
              </div>
              <div style={{ fontSize: '14px', color: '#0c4a6e' }}>Success Rate</div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ConsolidatedAIInsights;
